type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitCancelFullScreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  webkitCurrentFullScreenElement?: Element | null;
  webkitIsFullScreen?: boolean;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

let transientFullscreenExitDeadline = 0;
const DEFAULT_FULLSCREEN_REQUEST_TIMEOUT_MS = 2000;
const ANDROID_FIREFOX_FULLSCREEN_REQUEST_TIMEOUT_MS = 1200;
type FullscreenAttemptResult = 'success' | 'error' | 'timeout';

export const isAndroidFirefoxBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  return /Android/i.test(userAgent) && /Firefox\/\d+/i.test(userAgent);
};

export const isPageFullscreen = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  return Boolean(
    fullscreenDocument.fullscreenElement ||
      fullscreenDocument.webkitFullscreenElement ||
      fullscreenDocument.webkitCurrentFullScreenElement ||
      fullscreenDocument.webkitIsFullScreen ||
      fullscreenDocument.mozFullScreenElement ||
      fullscreenDocument.msFullscreenElement,
  );
};

export const suppressNextFullscreenAutoClose = (durationMs = 5000): void => {
  transientFullscreenExitDeadline = Date.now() + durationMs;
};

export const consumeTransientFullscreenExit = (): boolean => {
  if (Date.now() > transientFullscreenExitDeadline) {
    transientFullscreenExitDeadline = 0;
    return false;
  }

  transientFullscreenExitDeadline = 0;
  return true;
};

const waitForFullscreenAttempt = async (
  invokeRequest: () => Promise<void> | void,
  timeoutMs: number,
): Promise<FullscreenAttemptResult> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 'error';
  }

  return new Promise<FullscreenAttemptResult>((resolve) => {
    let settled = false;

    const cleanup = (result: FullscreenAttemptResult) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange as EventListener);
      document.removeEventListener('mozfullscreenchange', handleChange as EventListener);
      document.removeEventListener('MSFullscreenChange', handleChange as EventListener);
      document.removeEventListener('fullscreenerror', handleError);
      document.removeEventListener('webkitfullscreenerror', handleError as EventListener);
      document.removeEventListener('mozfullscreenerror', handleError as EventListener);
      document.removeEventListener('MSFullscreenError', handleError as EventListener);
      resolve(result);
    };

    const handleChange = () => {
      cleanup(isPageFullscreen() ? 'success' : 'error');
    };

    const handleError = () => {
      cleanup('error');
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange as EventListener);
    document.addEventListener('mozfullscreenchange', handleChange as EventListener);
    document.addEventListener('MSFullscreenChange', handleChange as EventListener);
    document.addEventListener('fullscreenerror', handleError);
    document.addEventListener('webkitfullscreenerror', handleError as EventListener);
    document.addEventListener('mozfullscreenerror', handleError as EventListener);
    document.addEventListener('MSFullscreenError', handleError as EventListener);

    const timeoutId = window.setTimeout(() => {
      cleanup(isPageFullscreen() ? 'success' : 'timeout');
    }, timeoutMs);

    try {
      const requestResult = invokeRequest();

      if (requestResult && typeof (requestResult as Promise<void>).then === 'function') {
        void Promise.resolve(requestResult)
          .then(() => {
            if (isPageFullscreen()) {
              cleanup('success');
            }
          })
          .catch(() => {
            cleanup('error');
          });
        return;
      }

      if (isPageFullscreen()) {
        cleanup('success');
      }
    } catch {
      cleanup('error');
    }
  });
};

export const requestPageFullscreen = async (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return false;
  }

  if (isPageFullscreen()) {
    return true;
  }

  const rootElement = document.documentElement as FullscreenCapableElement | null;

  if (!rootElement) {
    return false;
  }

  const timeoutMs = isAndroidFirefoxBrowser()
    ? ANDROID_FIREFOX_FULLSCREEN_REQUEST_TIMEOUT_MS
    : DEFAULT_FULLSCREEN_REQUEST_TIMEOUT_MS;

  if (rootElement.requestFullscreen) {
    const fullscreenWithHiddenNavigation = await waitForFullscreenAttempt(
      () => rootElement.requestFullscreen({ navigationUI: 'hide' }),
      timeoutMs,
    );

    if (fullscreenWithHiddenNavigation === 'success') {
      return true;
    }

    if (fullscreenWithHiddenNavigation === 'timeout') {
      return false;
    }

    return (
      (await waitForFullscreenAttempt(() => rootElement.requestFullscreen(), timeoutMs)) ===
      'success'
    );
  }

  if (rootElement.webkitRequestFullscreen) {
    return (
      (await waitForFullscreenAttempt(() => rootElement.webkitRequestFullscreen?.(), timeoutMs)) ===
      'success'
    );
  }

  if (rootElement.webkitRequestFullScreen) {
    return (
      (await waitForFullscreenAttempt(() => rootElement.webkitRequestFullScreen?.(), timeoutMs)) ===
      'success'
    );
  }

  if (rootElement.mozRequestFullScreen) {
    return (
      (await waitForFullscreenAttempt(() => rootElement.mozRequestFullScreen?.(), timeoutMs)) ===
      'success'
    );
  }

  if (rootElement.msRequestFullscreen) {
    return (
      (await waitForFullscreenAttempt(() => rootElement.msRequestFullscreen?.(), timeoutMs)) ===
      'success'
    );
  }

  return false;
};

export const exitPageFullscreen = async (): Promise<void> => {
  if (typeof document === 'undefined') {
    return;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  try {
    if (fullscreenDocument.exitFullscreen) {
      await fullscreenDocument.exitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitExitFullscreen) {
      await fullscreenDocument.webkitExitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitCancelFullScreen) {
      await fullscreenDocument.webkitCancelFullScreen();
      return;
    }

    if (fullscreenDocument.mozCancelFullScreen) {
      await fullscreenDocument.mozCancelFullScreen();
      return;
    }

    if (fullscreenDocument.msExitFullscreen) {
      await fullscreenDocument.msExitFullscreen();
    }
  } catch {
    // Ignore fullscreen exit failures so modal close is never blocked.
  }
};
